import { formatMoney } from "../../lib/money";

import { BarMiniChart } from "./svgPrimitives";

interface DailyPnLPoint {
  trade_date: string;
  pnl_amount: string;
}

interface DailyPnLChartProps {
  points: ReadonlyArray<DailyPnLPoint>;
  currency?: string;
  fill?: boolean;
  // When true, draw a left "$" value axis (max / 0 / min) and suppress the
  // bar chart's own corner labels so they don't overlap the first candle.
  axis?: boolean;
}

function compactMoney(value: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

export function DailyPnLChart({
  points,
  currency = "USD",
  fill,
  axis = false,
}: DailyPnLChartProps) {
  const values = points.map((p) => Number(p.pnl_amount));
  const labels = points.map((p) => p.trade_date);

  const bars = (
    <BarMiniChart
      values={values}
      testId="daily-pnl-chart"
      labels={labels}
      formatValue={(v) => formatMoney(v.toFixed(2), { currency })}
      fill={fill}
      hideMinMax={axis}
    />
  );

  if (!axis || values.length === 0) {
    return bars;
  }

  // Bars diverge around zero, so the axis reads max (top) / $0 / min (bottom).
  // The labels are positioned at the SAME fractional heights the bar chart uses
  // (mirroring svgPrimitives' domainWithZero + yScale, PAD_Y=12 of VB_H=192),
  // so the "$0" tick lines up with the chart's zero baseline instead of sitting
  // at a fixed vertical centre. The $0 tick is suppressed when zero coincides
  // with an endpoint (all-positive or all-negative data), where it would just
  // duplicate the max/min label.
  const domMax = Math.max(0, ...values);
  const domMin = Math.min(0, ...values);
  const effMax = domMin === 0 && domMax === 0 ? 1 : domMax;
  const range = effMax - domMin || 1;
  const PAD = 12 / 192;
  const topPct = (v: number): number =>
    (PAD + (1 - (v - domMin) / range) * (1 - 2 * PAD)) * 100;
  const showZero = domMin < 0 && domMax > 0;

  // The max/0/min labels are positioned at their true data heights, so when a
  // bound sits close to zero the "$0" tick collides with the max/min label.
  // Nudge the zero tick away from whichever endpoint it crowds (the endpoints
  // stay pinned to the chart's domain) so the figures never overlap. ~11% of
  // the axis height is roughly one label line at the widget's typical size.
  const MIN_GAP_PCT = 11;
  const maxTop = topPct(effMax);
  const minTop = topPct(domMin);
  let zeroTop = topPct(0);
  if (showZero) {
    if (zeroTop - maxTop < MIN_GAP_PCT) zeroTop = maxTop + MIN_GAP_PCT;
    if (minTop - zeroTop < MIN_GAP_PCT) zeroTop = minTop - MIN_GAP_PCT;
  }

  return (
    <div className="flex h-full w-full">
      <div className="relative w-12 shrink-0 pr-1 text-right text-[10px] tabular-nums text-on-surface-muted">
        <span
          className="absolute right-1 -translate-y-1/2"
          style={{ top: `${maxTop}%` }}
        >
          {compactMoney(domMax, currency)}
        </span>
        {showZero ? (
          <span
            className="absolute right-1 -translate-y-1/2"
            style={{ top: `${zeroTop}%` }}
          >
            {compactMoney(0, currency)}
          </span>
        ) : null}
        <span
          className="absolute right-1 -translate-y-1/2"
          style={{ top: `${minTop}%` }}
        >
          {compactMoney(domMin, currency)}
        </span>
      </div>
      <div className="min-w-0 flex-1">{bars}</div>
    </div>
  );
}
